import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Checkbox,
  Group,
  Modal,
  TextInput,
  Stack,
  Text,
  Divider,
  Title,
} from "@mantine/core";
import { toast } from "sonner";
import {
  IconLink,
  IconWorld,
  IconUser,
  IconInfoCircle,
} from "@tabler/icons-react";

import {
  useLinkAccountingSoftwareMutation,
  useTestPriorityConnectionMutation,
} from "../api/queries/organizationQueries";
import { IntegrationConnectButton } from "./integrations/IntegrationButtons";

/** Priority API auth uses username + fixed password literal "PAT" (personal access token). */
const PRIORITY_AUTH_PASSWORD = "PAT";

interface PriorityFormData {
  apiUrl: string;
  username: string;
  fncpatnames: string[];
}

const INITIAL_FORM_DATA: PriorityFormData = {
  apiUrl: "",
  username: "",
  fncpatnames: [],
};

const API_URL_PLACEHOLDER =
  "https://t.eu.priority-connect.online/odata/priority/tabbtd38.ini/usdemo";

const PRIORITY_DOCUMENT_TYPE_OPTIONS = [
  { code: "חי", label: "חשבונית לחו״ל" },
  { code: "חל", label: "חשבונית מס" },
  { code: "חלמ", label: "חשבונית לקוח מרכזת" },
  { code: "חק", label: "חשבונית קבלה" },
  { code: "עסק", label: "חשבונית עסקה" },
  { code: "EXP", label: "Export invoice (EI)" },
  { code: "GRV", label: "Tax invoice (GI)" },
] as const;

export function PriorityConnection() {
  const { mutate: linkAccountingSoftware, isPending: isLinkingPriority } =
    useLinkAccountingSoftwareMutation();
  const { mutate: testPriorityConnection, isPending: isTestingConnection } =
    useTestPriorityConnectionMutation();

  const [priorityModalOpened, setPriorityModalOpened] = useState(false);
  const [formData, setFormData] = useState<PriorityFormData>(INITIAL_FORM_DATA);

  const isFormValid = useMemo(
    () =>
      formData.apiUrl.trim() !== "" &&
      formData.username.trim() !== "" &&
      formData.fncpatnames.length > 0,
    [formData],
  );

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleCloseModal = useCallback(() => {
    setPriorityModalOpened(false);
    resetForm();
  }, [resetForm]);

  const handleOpenModal = useCallback(() => {
    setPriorityModalOpened(true);
  }, []);

  const handleFormChange = useCallback(
    (field: keyof PriorityFormData) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      },
    [],
  );

  const handlePriorityConnect = useCallback(() => {
    if (!isFormValid) {
      toast.error("Please fill in all fields");
      return;
    }

    // Validate URL format
    try {
      new URL(formData.apiUrl.trim());
    } catch {
      toast.error("Please enter a valid API URL");
      return;
    }

    const base64Token = btoa(
      `${formData.username.trim()}:${PRIORITY_AUTH_PASSWORD}`,
    );

    const apiUrl = formData.apiUrl.trim();

    // First test the connection
    testPriorityConnection(
      {
        api_url: apiUrl,
        base64_token: base64Token,
      },
      {
        onSuccess: () => {
          // Connection test passed, now save the credentials
          linkAccountingSoftware(
            {
              name: "Priority",
              api_url: apiUrl,
              base64_token: base64Token,
              fncpatnames: formData.fncpatnames,
            },
            {
              onSuccess: () => {
                toast.success("Successfully connected to Priority");
                handleCloseModal();
              },
              onError: (err: any) => {
                const detail = err?.response?.data?.detail;
                const errorMessage =
                  (typeof detail === "object" && detail?.message
                    ? detail.message
                    : typeof detail === "string"
                      ? detail
                      : null) ||
                  err?.message ||
                  "Failed to save credentials";
                toast.error("Failed to connect to Priority", {
                  description: errorMessage,
                });
              },
            },
          );
        },
        onError: (err: any) => {
          // Connection test failed, don't save credentials
          const detail = err?.response?.data?.detail;
          const errorMessage =
            (typeof detail === "object" && detail?.message
              ? detail.message
              : typeof detail === "string"
                ? detail
                : null) ||
            err?.message ||
            "Connection test failed. Please check your credentials and API URL.";
          toast.error("Connection test failed", {
            description: errorMessage,
          });
        },
      },
    );
  }, [
    formData,
    isFormValid,
    testPriorityConnection,
    linkAccountingSoftware,
    handleCloseModal,
  ]);

  return (
    <>
      <IntegrationConnectButton onClick={handleOpenModal} />

      <Modal
        opened={priorityModalOpened}
        onClose={handleCloseModal}
        title={
          <Group gap="xs">
            <IconLink size={20} />
            <Title order={4}>Connect to Priority</Title>
          </Group>
        }
        centered
        closeOnClickOutside={!isLinkingPriority && !isTestingConnection}
        closeOnEscape={!isLinkingPriority && !isTestingConnection}
        size="lg"
        radius="md"
        padding="xl"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
            Enter your Priority API credentials to connect your account. your
            data is encrypted and stored securely.This will allow Datamint to
            sync invoices and data from your Priority system.
          </Text>

          <Divider />

          <Stack gap="md">
            <TextInput
              label="API URL"
              placeholder={API_URL_PLACEHOLDER}
              value={formData.apiUrl}
              onChange={handleFormChange("apiUrl")}
              required
              disabled={isLinkingPriority || isTestingConnection}
              description="Enter your Priority API endpoint URL (do not include entity names - they will be added automatically)"
              leftSection={<IconWorld size={18} />}
              radius="md"
              size="md"
              error={
                formData.apiUrl &&
                formData.apiUrl.trim() !== "" &&
                (() => {
                  try {
                    new URL(formData.apiUrl.trim());
                    return null;
                  } catch {
                    return "Please enter a valid URL";
                  }
                })()
              }
            />
            <TextInput
              label="Username"
              placeholder="Enter your Priority username"
              value={formData.username}
              onChange={handleFormChange("username")}
              required
              disabled={isLinkingPriority || isTestingConnection}
              autoComplete="username"
              leftSection={<IconUser size={18} />}
              radius="md"
              size="md"
            />
            <Checkbox.Group
              label="Priority document types"
              description="Select one or more document types to sync"
              value={formData.fncpatnames}
              onChange={(selectedCodes) =>
                setFormData((prev) => ({
                  ...prev,
                  fncpatnames: [...selectedCodes],
                }))
              }
              required
            >
              <Stack gap="xs" mt="xs">
                {PRIORITY_DOCUMENT_TYPE_OPTIONS.map((option) => (
                  <Checkbox
                    key={option.code}
                    value={option.code}
                    label={`${option.label}`}
                    disabled={isLinkingPriority || isTestingConnection}
                  />
                ))}
              </Stack>
            </Checkbox.Group>
          </Stack>

          <Group
            p="md"
            style={{
              backgroundColor: "var(--mantine-color-blue-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: "1px solid var(--mantine-color-blue-2)",
            }}
            gap="xs"
          >
            <IconInfoCircle size={18} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              Your credentials are encrypted and stored securely. They will only
              be used to access your Priority API.
            </Text>
          </Group>

          <Divider />

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={isLinkingPriority || isTestingConnection}
              radius="md"
              size="md"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePriorityConnect}
              loading={isLinkingPriority || isTestingConnection}
              disabled={
                !isFormValid || isLinkingPriority || isTestingConnection
              }
              leftSection={
                !isLinkingPriority &&
                !isTestingConnection && <IconLink size={16} />
              }
              variant="gradient"
              gradient={{ from: "violet", to: "cyan", deg: 90 }}
              radius="md"
              size="md"
            >
              {isTestingConnection
                ? "Testing connection..."
                : isLinkingPriority
                  ? "Connecting..."
                  : "Connect"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
